import { useState } from 'react';
import { Card } from '@design/Card';
import { Button } from '@design/Button';
import { Callout } from '@design/Callout';
import { clsx } from 'clsx';
import type { PracticeQuestion } from '@curriculum/types';
import { gradeAnswer, normalise } from '@/lib/gradeAnswer';

type Props = {
  questions: PracticeQuestion[];
};

type Answer = {
  value: string;
  correct: boolean;
};

/** Confidence level the student can optionally declare before checking. */
type Confidence = 'high' | 'medium' | 'low';

type QuestionState = {
  answer?: Answer;
  confidence?: Confidence;
};

/**
 * ChapterQuiz — interactive self-assessment using the same practice data as
 * PracticeBlock, but quiz-oriented: show all questions at once (or page through
 * them), let students pick/type an answer, reveal correctness + explanation on
 * "Check", and display a running score.
 *
 * Design principles:
 * - MCQ → clickable option buttons (radio-style; no form needed).
 * - Numeric / short / journal-entry → text input + "Check" button.
 * - Once answered, the correct option is highlighted green; wrong is red.
 * - Students can navigate freely — answering is not mandatory to advance.
 * - Score shown at top as "X / Y answered correctly" with a progress bar.
 * - Confidence calibration (optional): tap High/Medium/Low before checking;
 *   metacognitive nudge shown when High+Wrong or Low+Right.
 * - Progress bar and accent use var(--subject-color) set by the parent container.
 */
export function ChapterQuiz({ questions }: Props) {
  const [states, setStates] = useState<Record<string, QuestionState>>({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const totalAnswered = Object.values(states).filter((s) => s.answer !== undefined).length;
  const totalCorrect = Object.values(states).filter((s) => s.answer?.correct).length;
  const allDone = totalAnswered === questions.length;

  const currentRaw = questions[currentIdx];
  if (!currentRaw) return null;
  const current = currentRaw;

  const qState = states[current.id] ?? {};
  const verdict = qState.answer;
  const confidence = qState.confidence;
  const inputValue = inputValues[current.id] ?? '';

  function recordAnswer(qId: string, value: string, correctAnswer: string, kind: string) {
    const correct = gradeAnswer(value, correctAnswer, kind);
    setStates((prev) => ({
      ...prev,
      [qId]: { ...prev[qId], answer: { value, correct } },
    }));
  }

  function setConfidence(qId: string, level: Confidence) {
    // Only allow setting confidence before answering.
    setStates((prev) => {
      if (prev[qId]?.answer) return prev; // already answered — no-op
      return { ...prev, [qId]: { ...prev[qId], confidence: level } };
    });
  }

  function handleMCQSelect(opt: string) {
    if (states[current.id]?.answer) return; // already answered
    recordAnswer(current.id, opt, current.correct, 'mcq');
  }

  function handleCheck() {
    if (!inputValue.trim() || states[current.id]?.answer) return;
    recordAnswer(current.id, inputValue.trim(), current.correct, current.kind);
  }

  function handleReset() {
    setStates({});
    setInputValues({});
    setCurrentIdx(0);
  }

  // Metacognition note: shown after reveal when confidence is set.
  function metacognitionNote(
    v: Answer,
    conf: Confidence | undefined,
  ): string | null {
    if (!conf) return null;
    if (conf === 'high' && !v.correct)
      return '⚠ This is a misconception worth revisiting.';
    if (conf === 'low' && v.correct)
      return 'Nice — that was a real recall.';
    return null;
  }

  const metaNote = verdict ? metacognitionNote(verdict, confidence) : null;

  return (
    <Card className="!p-0 overflow-hidden shadow-card-raised !rounded-card-lg">
      {/* Quiz header with score */}
      <header className="px-5 sm:px-6 py-4 border-b border-ink-200/80 bg-gradient-to-b from-ink-50 to-cream">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: 'var(--subject-color)' }}>
              Quiz
            </div>
            <p className="text-ink-900 mt-0.5 text-sm">
              Question {currentIdx + 1} of {questions.length}
              {' · '}
              <span className="text-ink-500 capitalize">{current.difficulty}</span>
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-600 font-mono tabular-nums">
              {totalCorrect} / {questions.length} correct
            </div>
            {totalAnswered > 0 && (
              <div className="mt-1.5 h-1.5 w-28 rounded-full bg-ink-200/80 overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-500 ease-out-soft"
                  style={{
                    width: `${(totalCorrect / questions.length) * 100}%`,
                    backgroundColor: 'var(--subject-color)',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Question step pills — single scrollable row, active dot scrolled into view */}
        <div
          className="flex gap-1.5 overflow-x-auto mt-1 py-1 px-0.5"
          style={{ scrollbarWidth: 'none' }}
          ref={(el) => {
            if (!el) return;
            const active = el.querySelector<HTMLButtonElement>('[data-active="true"]');
            if (!active) return;
            // Horizontal-only: scrollIntoView() would scroll the whole page down
            // to the quiz on first render.
            const rowRect = el.getBoundingClientRect();
            const dotRect = active.getBoundingClientRect();
            el.scrollTo({
              left: el.scrollLeft + (dotRect.left - rowRect.left) - (rowRect.width - dotRect.width) / 2,
              behavior: 'smooth',
            });
          }}
        >
          {questions.map((q, i) => {
            const ans = states[q.id]?.answer;
            const isActive = i === currentIdx;
            return (
              <button
                key={q.id}
                data-active={isActive ? 'true' : undefined}
                onClick={() => setCurrentIdx(i)}
                className={clsx(
                  'flex-shrink-0 w-7 h-7 rounded-full text-[11px] font-semibold tabular-nums transition-[background-color,color,box-shadow] duration-150',
                  'focus-visible:ring-2 focus-visible:ring-offset-1',
                  ans?.correct && 'bg-econ-500 text-white',
                  ans && !ans.correct && 'bg-credit-500 text-white',
                  ans && isActive && 'ring-2 ring-offset-2 ring-offset-cream ring-ink-400',
                  !ans && !isActive && 'bg-cream text-ink-600 ring-1 ring-inset ring-ink-200 hover:bg-ink-50 hover:ring-ink-300',
                )}
                style={
                  isActive && !ans
                    ? { backgroundColor: 'var(--subject-color)', color: '#fff' }
                    : undefined
                }
                aria-label={`Go to question ${i + 1}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </header>

      {/* Question body */}
      <div className="px-5 sm:px-6 py-6 bg-cream min-h-[220px]">
        <p className="text-ink-900 text-[1.05rem] mb-5 leading-relaxed font-medium text-pretty">{current.prompt}</p>

        {/* Confidence calibration — optional, skippable, shown before answer only */}
        {!verdict && (
          <ConfidencePicker
            value={confidence}
            onChange={(level) => setConfidence(current.id, level)}
          />
        )}

        {current.kind === 'mcq' && current.options ? (
          <MCQOptions
            options={current.options}
            correct={current.correct}
            verdict={verdict}
            onSelect={handleMCQSelect}
          />
        ) : (
          <FreeAnswer
            kind={current.kind}
            value={inputValue}
            verdict={verdict}
            correct={current.correct}
            onChange={(v) =>
              setInputValues((prev) => ({ ...prev, [current.id]: v }))
            }
            onCheck={handleCheck}
          />
        )}

        {verdict && (
          <div className="mt-5 flex flex-col gap-2 animate-pop-in motion-reduce:animate-none">
            {verdict.correct ? (
              <Callout tone="success" title="Correct!">
                {current.explanation}
              </Callout>
            ) : (
              <Callout
                tone="danger"
                title={`Not quite — the answer is "${current.correct}".`}
              >
                {current.explanation}
              </Callout>
            )}
            {metaNote && (
              <p className="text-xs text-ink-500 italic px-1">{metaNote}</p>
            )}
          </div>
        )}
      </div>

      {/* Footer navigation */}
      <footer className="px-4 sm:px-6 py-3.5 bg-ink-50/80 border-t border-ink-200/80 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          disabled={currentIdx === 0}
          onClick={() => setCurrentIdx((i) => i - 1)}
        >
          ← Prev
        </Button>

        {allDone ? (
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-ink-700">
              {totalCorrect === questions.length
                ? 'Perfect score!'
                : `${totalCorrect} / ${questions.length} — review the ones in red.`}
            </span>
            <Button variant="secondary" onClick={handleReset}>
              Retake
            </Button>
          </div>
        ) : (
          <span className="text-xs text-ink-500">
            {totalAnswered === 0
              ? 'Answer to see your score.'
              : `${questions.length - totalAnswered} left`}
          </span>
        )}

        <Button
          variant={currentIdx === questions.length - 1 ? 'secondary' : 'primary'}
          disabled={currentIdx === questions.length - 1}
          onClick={() => setCurrentIdx((i) => i + 1)}
        >
          Next →
        </Button>
      </footer>
    </Card>
  );
}

/* ---- sub-components ---- */

function ConfidencePicker({
  value,
  onChange,
}: {
  value: Confidence | undefined;
  onChange: (level: Confidence) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-5">
      <span className="text-xs text-ink-500 shrink-0">How sure are you?</span>
      {(['high', 'medium', 'low'] as Confidence[]).map((level) => (
        <button
          key={level}
          type="button"
          onClick={() => onChange(level)}
          className={clsx(
            'px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150',
            value === level
              ? level === 'high'
                ? 'bg-econ-100 border-econ-200 text-econ-600'
                : level === 'medium'
                  ? 'bg-warn-100 border-warn-500 text-ink-700'
                  : 'bg-ink-100 border-ink-400 text-ink-700'
              : 'border-ink-200 text-ink-500 hover:border-ink-300 hover:bg-ink-50',
          )}
          aria-pressed={value === level}
        >
          {level.charAt(0).toUpperCase() + level.slice(1)}
        </button>
      ))}
    </div>
  );
}

function MCQOptions({
  options,
  correct,
  verdict,
  onSelect,
}: {
  options: string[];
  correct: string;
  verdict?: Answer;
  onSelect: (opt: string) => void;
}) {
  return (
    <div className="grid gap-2.5">
      {options.map((opt) => {
        const isSelected = verdict?.value === opt;
        const isCorrectOpt = normalise(opt) === normalise(correct);
        const revealed = !!verdict;

        return (
          <button
            key={opt}
            disabled={revealed}
            onClick={() => onSelect(opt)}
            className={clsx(
              'w-full text-left px-4 py-3.5 rounded-xl border transition-[border-color,background-color,box-shadow,transform] duration-150 text-[15px] leading-snug',
              !revealed &&
                'border-ink-200 bg-white/70 text-ink-900 shadow-soft hover:border-accent-500 hover:bg-accent-50 hover:-translate-y-px active:translate-y-0 motion-reduce:hover:translate-y-0',
              revealed &&
                isCorrectOpt &&
                'border-econ-500/60 bg-econ-50 text-econ-600 font-semibold ring-1 ring-econ-500/20',
              revealed &&
                isSelected &&
                !isCorrectOpt &&
                'border-credit-500/40 bg-credit-100/40 text-ink-900 ring-1 ring-credit-500/15',
              revealed &&
                !isSelected &&
                !isCorrectOpt &&
                'border-ink-200 opacity-60 text-ink-600',
            )}
          >
            {revealed && isCorrectOpt && (
              <span className="mr-1.5 text-econ-600">✓</span>
            )}
            {revealed && isSelected && !isCorrectOpt && (
              <span className="mr-1.5 text-credit-600">✗</span>
            )}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function FreeAnswer({
  kind,
  value,
  verdict,
  correct,
  onChange,
  onCheck,
}: {
  kind: string;
  value: string;
  verdict?: Answer;
  correct: string;
  onChange: (v: string) => void;
  onCheck: () => void;
}) {
  const placeholder =
    kind === 'numeric'
      ? 'Enter a number, e.g. 24500'
      : kind === 'journal-entry'
        ? 'Describe the journal entry…'
        : 'Type your answer…';

  // Show the authoritative correct value after reveal for free-answer
  // so the student can compare against what was expected.
  const displayValue = verdict ? verdict.value : value;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <input
          type="text"
          inputMode={kind === 'numeric' ? 'decimal' : 'text'}
          placeholder={placeholder}
          value={displayValue}
          disabled={!!verdict}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCheck();
          }}
          className={clsx(
            'flex-1 min-w-0 px-3.5 py-2.5 rounded-xl border text-sm shadow-[inset_0_1px_2px_rgba(27,31,59,0.05)] focus:outline-none',
            'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
            verdict
              ? verdict.correct
                ? 'border-econ-500/50 bg-econ-50'
                : 'border-credit-500/40 bg-credit-100/20'
              : 'border-ink-300 bg-cream',
          )}
        />
        {!verdict && (
          <Button
            type="button"
            disabled={!value.trim()}
            onClick={onCheck}
          >
            Check
          </Button>
        )}
      </div>
      {/* After a wrong free-answer reveal, show the expected answer clearly */}
      {verdict && !verdict.correct && (
        <p className="text-xs text-ink-500">
          Expected: <span className="font-medium text-ink-700">{correct}</span>
        </p>
      )}
    </div>
  );
}

/* grading helpers now live in src/lib/gradeAnswer.ts (shared with PracticeBlock) */
